import * as React from 'react'
import * as ReactSwipe from 'react-swipe'
import { IconButton } from '@doordash/component-button'
import { InlineChildren, Media, Spacing } from '@doordash/design-language'
import Pagination from '@doordash/component-pagination'
import { useCarousel } from './context'
import styled from 'styled-components'

type CarouselProps = {
  onSwipe?: <T>(...T) => Promise<void>
  isFullScreen?: boolean
} & React.ComponentPropsWithoutRef<'div'>

/**
 * Renders its children into a carousel. Can be swiped through
 * with touch when on mobile.
 * Children of carousel can pass in isFooterHidden to hide/show
 * the pagination footer and renderFooterContent to render custom
 * footer content.
 * @param onSwipe Callback for when carousel is progressed through
 * @returns Carousel
 **/
const Carousel: React.FC<CarouselProps> = ({
  children,
  onSwipe = () => {},
  ...props
}) => {
  const [reactSwipeEl, setReactSwipeEl] = React.useState<ReactSwipe>()
  const { state, dispatch } = useCarousel()

  // Number of child elements in the Carousel
  const count = (React.Children.toArray(children).filter(child =>
    React.isValidElement(child)
  ) as React.ReactElement[]).length

  const swipeOptions = React.useMemo(
    () => ({
      continuous: false,
      startSlide: state.activeIndex,
      transitionEnd: index => {
        // Condition handles edge case where user partially swipes and ends
        // up staying on the same slide. Only dispatch when a full swipe is performed.
        if (index !== state.activeIndex) {
          dispatch({ type: 'set', payload: index })
          onSwipe(index, children[index].props.id)
        }
      },
    }),
    [state.activeIndex, dispatch, onSwipe, children]
  )

  const { isFooterHidden, renderFooterContent } =
    React.useMemo(() => children[state.activeIndex].props.children.props, [
      children,
      state.activeIndex,
    ]) || {}

  return (
    <CarouselContainer {...props}>
      <ReactSwipe swipeOptions={swipeOptions} ref={ref => setReactSwipeEl(ref)}>
        {children}
      </ReactSwipe>

      <ControlsContainer {...props}>
        {renderFooterContent && renderFooterContent()}
        {!isFooterHidden && (
          <InlineChildren
            justifyContent={InlineChildren.Justification.SpaceBetween}
          >
            <IconButtonWrapper
              isVisible={state.activeIndex !== 0}
              isFullScreen={props.isFullScreen}
            >
              <IconButton
                iconType={IconButton.IconTypes.ArrowLeft}
                accessibilityLabel={'Arrow Left'}
                isInline
                onClick={() => {
                  dispatch({ type: 'decrement' })
                  reactSwipeEl.prev()
                  const index = state.activeIndex - 1
                  onSwipe(index, children[index].props.id)
                }}
              />
            </IconButtonWrapper>
            <Pagination
              accessibilityLabel={'Pagination'}
              indicatorAccessibilityLabelPrefix={'Pagination'}
              count={count}
              selectedIndex={state.activeIndex}
              shape={Pagination.Shapes.Dot}
              onSelect={index => {
                dispatch({ type: 'set', payload: index })
                onSwipe(index, children[index].props.id)
              }}
            />
            <IconButtonWrapper
              isVisible={state.activeIndex !== count - 1}
              isFullScreen={props.isFullScreen}
            >
              <IconButton
                iconType={IconButton.IconTypes.ArrowRight}
                accessibilityLabel={'Arrow Right'}
                isInline
                onClick={() => {
                  dispatch({ type: 'increment' })
                  reactSwipeEl.next()
                  const index = state.activeIndex + 1
                  onSwipe(index, children[index].props.id)
                }}
              />
            </IconButtonWrapper>
          </InlineChildren>
        )}
      </ControlsContainer>
    </CarouselContainer>
  )
}

const CONTROLS_CONTAINER_HEIGHT = 80
const CarouselContainer = styled.div`
  ${Media.Mobile`
        position: relative;
        height: ${(props: { isFullScreen: boolean }) =>
          props.isFullScreen
            ? `calc(100vh - ${CONTROLS_CONTAINER_HEIGHT * 1.5}px)`
            : `auto`}};
    `}
`

const ControlsContainer = styled.div`
  ${Media.Mobile`
        position: ${(props: { isFullScreen: boolean }) =>
          props.isFullScreen ? `fixed` : `relative`}};
        background: ${(props: { isFullScreen: boolean }) =>
          props.isFullScreen ? `white` : `none`}};
        width: 100%;
        left: 0;
        bottom: 0;
        padding: 0 ${Spacing.XSmall};
    `}
`

type IconButtonWrapperProps = {
  isVisible: boolean
  isFullScreen: boolean
}

const IconButtonWrapper = styled.div<IconButtonWrapperProps>(
  ({ isFullScreen, isVisible }) => ({
    visibility: isFullScreen && isVisible ? `visible` : `hidden`,
    button: {
      background: isFullScreen ? `inherit` : `none`,
    },
  })
)

Carousel.defaultProps = {
  isFullScreen: true,
}

export default Carousel
